<template>
  <div
    v-if="isLocaleSelectorEnabled && localeOptions.length > 1"
    class="caption-bold flex items-center gap-8"
    data-test="language-switch-links"
  >
    <template v-for="(option, idx) in localeOptions" :key="option.bcp47Locale">
      <span v-if="idx > 0" aria-hidden="true">|</span>

      <span
        v-if="option.isCurrent"
        class="underline"
        data-test="language-switch-current"
      >
        {{ option.label }}
      </span>

      <RevLink
        v-else
        :data-test="`language-switch-link-${option.bcp47Locale}`"
        :hreflang="option.bcp47Locale.split('-')[0]"
        :to="option.href"
        :underlined="false"
        @click="handleLanguageLinkClick(option.bcp47Locale)"
      >
        {{ option.label }}
      </RevLink>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { Bcp47Locale } from '@backmarket/http-api/src/standards/Locale'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { RevLink } from '@ds/components/Link'

import { useLinkResolver } from '~/composables/useLinkResolver'
import { HOME } from '~/scopes/home/route-names'
import { isEsUsLocaleSelectorEnabled } from '~/scopes/navigation/utils/isEsUsLocaleSelectorEnabled'

import { languagesDict } from '../../utils/countryDisplayName'

const i18n = useI18n()
const { market } = useMarketplace()
const experiments = useExperiments()
const { getLink } = useLinkResolver()
const { trackClick } = useTracking()

const isLocaleSelectorEnabled = isEsUsLocaleSelectorEnabled(
  experiments['experiment.enableEsUsLocaleSelector'],
  market.countryCode,
)

const localeOptions = computed(() => {
  if (!market.locales || market.locales.length < 2) return []

  return market.locales.map((locale) => {
    const bcp47Locale = toBcp47Locale(locale)
    const localePrefix = locale.split('_')[0]

    return {
      bcp47Locale,
      isCurrent: bcp47Locale === i18n.currentLocale,
      label: languagesDict[localePrefix] || localePrefix,
      href:
        getLink({
          link: { name: HOME },
          countryCode: MarketCountryCode[market.countryCode],
          overrideLocale: bcp47Locale,
        })?.href ?? '#',
    }
  })
})

function handleLanguageLinkClick(targetLocale: Bcp47Locale) {
  trackClick({
    name: 'language_switch',
    zone: 'header',
    value: targetLocale,
  })
}
</script>
